import { KiTagListOptions } from '../schemas';

type KiListRow = {
  queryParameter: {
    name: string;
    value: string | null;
  };
};

type KiListRowWithKiCode = KiListRow & {
  queryParameter: { name: 'kiList'; value: string };
};

/**
 * 平均/合計行（value: null または 'null'）を除く kiList 行の機種コードか
 */
export const isKiListKiCodeValue = (value: string | null): value is string =>
  value !== null && value !== 'null';

/**
 * 平均/合計行を除く kiList 行か
 */
export const isKiListRowWithKiCode = <T extends KiListRow>(
  row: T
): row is T & KiListRowWithKiCode =>
  row.queryParameter.name === 'kiList' &&
  isKiListKiCodeValue(row.queryParameter.value);

/**
 * 平均/合計行を除く kiList 行のみに絞り込む
 */
export const filterKiListRows = <T extends KiListRow>(
  rows: T[]
): Array<T & KiListRowWithKiCode> => rows.filter(isKiListRowWithKiCode);

/**
 * メインテーブルから kiList 行の機種コード一覧を抽出する
 */
export const extractMainTableKiCodes = (rows: KiListRow[]): string[] =>
  filterKiListRows(rows).map((row) => row.queryParameter.value);

/**
 * 対象外機種数 = 合計機種数 − 選択タグ union に含まれる機種数
 */
export const calcExcludedKiTagKiCount = (
  mainTableKiCodes: string[],
  selectedTags: KiTagListOptions[]
): number => {
  const allTagKiList = new Set(selectedTags.flatMap((tag) => tag.kiList ?? []));

  const taggedKiCount = mainTableKiCodes.filter((kiCode) =>
    allTagKiList.has(kiCode)
  ).length;

  return mainTableKiCodes.length - taggedKiCount;
};

/**
 * 対象外機種平均合計行の fetch / 表示が必要かどうか
 */
export const hasExcludedKiTagAverageTarget = (
  mainTableKiCodes: string[],
  selectedTags: KiTagListOptions[]
): boolean => calcExcludedKiTagKiCount(mainTableKiCodes, selectedTags) > 0;

/**
 * 選択タグに紐づく機種のうち、メインテーブル（検索結果）に存在する機種コード一覧。
 * 詳細条件の kiList とタグの kiList の積集合。
 */
export const getTaggedKiCodesInMainTable = (
  mainTableKiCodes: string[],
  selectedTags: KiTagListOptions[]
): string[] => {
  const allTagKiList = new Set(selectedTags.flatMap((tag) => tag.kiList ?? []));

  return mainTableKiCodes.filter((kiCode) => allTagKiList.has(kiCode));
};

/**
 * 単一タグに紐づく機種のうち、メインテーブルに存在する機種があるか。
 * 個別タグ平均行の fetch 可否判定に使う。
 */
export const hasTaggedKiMatchForTag = (
  mainTableKiCodes: string[],
  tag: KiTagListOptions
): boolean => {
  const tagKiList = new Set(tag.kiList ?? []);
  return mainTableKiCodes.some((kiCode) => tagKiList.has(kiCode));
};

/**
 * 機種タグ平均合計行の fetch / 表示が必要かどうか。
 * 詳細条件で絞った機種とタグの積集合が 0 件なら false（誤った平均行を出さない）。
 */
export const hasTaggedKiMatchInMainTable = (
  mainTableKiCodes: string[],
  selectedTags: KiTagListOptions[]
): boolean =>
  getTaggedKiCodesInMainTable(mainTableKiCodes, selectedTags).length > 0;

export type FilterTableRowsBySelectedKiTagsOptions = {
  /**
   * 積集合判定に使う機種コード。
   * 省略時は rows から抽出する。機種名フィルタ後の rows では API 生データの機種コードを渡す。
   */
  mainTableKiCodes?: string[];
};

/**
 * 選択タグでメインテーブル行を絞り込む（機種集計・機種別商圏で共通）。
 *
 * 詳細条件の機種とタグが矛盾する（積集合 0 件）場合:
 * - 機種行・平均合計行をすべて非表示にする
 * - fetch 側の hasTaggedKiMatchInMainTable と同じ判定基準を使う
 */
export const filterTableRowsBySelectedKiTags = <TRow extends KiListRow>(
  rows: TRow[],
  selectedTags: KiTagListOptions[],
  options?: FilterTableRowsBySelectedKiTagsOptions
): TRow[] => {
  if (selectedTags.length === 0) {
    return rows;
  }

  const mainTableKiCodes =
    options?.mainTableKiCodes ?? extractMainTableKiCodes(rows);
  if (!hasTaggedKiMatchInMainTable(mainTableKiCodes, selectedTags)) {
    return [];
  }

  const allTagKiList = new Set(selectedTags.flatMap((tag) => tag.kiList ?? []));

  return rows.filter((row) => {
    const { value } = row.queryParameter;

    // 平均/合計行（value: null）
    if (value == null) {
      return true;
    }

    // 機種コード行（kiList）のみタグ union で絞り込む
    if (isKiListRowWithKiCode(row)) {
      return allTagKiList.has(row.queryParameter.value);
    }

    if (row.queryParameter.name === 'kiList') {
      // 機種集計の平均行（value: 'null'）はタグ選択時は非表示
      return false;
    }

    // メーカー等 kiList 以外の行はタグ絞り込みの対象外として維持
    return true;
  });
};
